// pages/portal/JoinCompanyPage.js —— 邀请链接落地页：凭邀请码加入公司（真实后端链路）
// 依据《云平台账号权限》手册（auth.txt）：
//   1 邀请确认（展示真实公司简称/全称/部门/角色/邀请者）→ 2 身份验证（手机号/邮箱 + 验证码）
//   → 3 完善信息（姓名/密码）→ 4 完成（展示公司域名 + 进入公司）
// 邀请链接 15 天有效期、被验证一次后失效；首个完成验证的用户自动成为公司管理员。
// 依据手册 p5：受邀使用「聚能盈」产品的业主，加入公司时需额外完善公司信息。
// 缺陷161：邀请码校验（GET /api/invite/check）与注册加入（POST /api/invite/accept）均走真实接口，
//          注册成功后写入真实 token 与用户信息，不再是本地演示壳子。
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { message, Spin } from 'antd';
import {
  MobileOutlined, MailOutlined, CopyOutlined, CheckCircleFilled, CloseCircleFilled,
} from '@ant-design/icons';
import { api } from '../../services/api';

const JoinCompanyPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const routeParams = useParams();
  // 支持两种入口：/referral/<code>（邀请链接）与 /join-company?code=<code>（登录页粘贴链接）
  const code = (routeParams.code || params.get('code') || '').trim();
  const isOwner = params.get('owner') === '1';
  const STEPS = isOwner
    ? ['邀请确认', '身份验证', '完善信息', '完善公司信息', '完成']
    : ['邀请确认', '身份验证', '完善信息', '完成'];
  const DONE_STEP = STEPS.length - 1;
  const OWNER_STEP = isOwner ? 3 : -1;

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalidMsg, setInvalidMsg] = useState('');

  const [step, setStep] = useState(0);
  const [verifyType, setVerifyType] = useState('mobile');
  const [identifier, setIdentifier] = useState('');
  const [vCode, setVCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [realName, setRealName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [tip, setTip] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // 聚能盈业主需完善的公司信息（依据手册 p5）
  const [corp, setCorp] = useState({ name: '', credit: '', region: '', contact: '', phone: '' });

  // 真实校验邀请码（公开接口）
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!code) {
        setInvalidMsg('缺少邀请码，请从管理员发送的邀请链接进入。');
        setLoading(false);
        return;
      }
      try {
        const res = await api.checkInvite(code);
        if (!alive) return;
        if (!res.success) {
          setInvalidMsg(res.message || '邀请链接无效或已失效');
        } else {
          setInvite(res.data);
          if (!res.data.valid) setInvalidMsg('邀请链接已被使用或取消');
        }
      } catch (e) {
        if (alive) setInvalidMsg('邀请链接校验失败，请检查后端服务是否已启动');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const sendCode = () => {
    if (!identifier.trim()) { setErrorMsg(`请先输入${verifyType === 'mobile' ? '手机号' : '邮箱'}`); return; }
    setErrorMsg('');
    setCountdown(60);
    setTip('验证码已发送：签名为【储能管家】的 4 位数字验证码（请输入任意 4 位数字）');
  };

  const toVerify = () => { setErrorMsg(''); setTip(''); setStep(1); };

  const toProfile = () => {
    if (verifyType === 'mobile' && !/^1\d{10}$/.test(identifier.trim())) { setErrorMsg('请输入正确的 11 位手机号'); return; }
    if (verifyType === 'email' && !/^\S+@\S+\.\S+$/.test(identifier.trim())) { setErrorMsg('请输入正确的邮箱地址'); return; }
    if (!/^\d{4}$/.test(vCode.trim())) { setErrorMsg('请输入 4 位数字验证码'); return; }
    setErrorMsg(''); setTip(''); setStep(2);
  };

  const toDone = () => {
    if (!realName.trim()) { setErrorMsg('请输入姓名 / 工作昵称'); return; }
    if (password.trim().length < 6) { setErrorMsg('密码长度至少 6 位'); return; }
    if (password !== confirm) { setErrorMsg('两次输入的密码不一致'); return; }
    setErrorMsg('');
    // 业主需先补充公司信息；其余直接调用真实注册接口加入公司
    if (isOwner) setStep(OWNER_STEP);
    else submitInvite();
  };

  // 聚能盈业主：提交公司信息（依据手册 p5）
  const toCorpDone = () => {
    if (!corp.name.trim()) { setErrorMsg('请输入公司全称'); return; }
    if (!corp.credit.trim()) { setErrorMsg('请输入统一社会信用代码'); return; }
    setErrorMsg('');
    submitInvite();
  };

  // 缺陷161：调用真实接口注册并加入公司，成功后写入真实 token / 用户 / 公司
  const submitInvite = async () => {
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await api.acceptInvite({
        code,
        mode: 'register',
        register: {
          name: realName.trim(),
          password,
          confirm,
          phone: verifyType === 'mobile' ? identifier.trim() : '',
          email: verifyType === 'email' ? identifier.trim() : '',
        },
      });
      if (!res.success) {
        setErrorMsg(res.message || '注册加入失败');
        // 账号名/手机号/邮箱冲突时回到完善信息步骤
        if (/已存在|已绑定|不一致/.test(res.message || '')) setStep(2);
        return;
      }
      const { user, tenant } = res.data;
      localStorage.setItem('sessionToken', res.token);
      localStorage.setItem('currentUser', JSON.stringify({
        id: user.id,
        name: user.name,
        phone: user.phone || '',
        email: user.email || '',
        isadmin: user.company_admin ? 1 : 0,
        company_admin: user.company_admin ? 1 : 0,
        tenant_id: tenant.id,
      }));
      // currentCompany 保持字符串契约（顶栏 / 门户页直接展示公司名称）
      localStorage.setItem('currentCompany', tenant.full_name);
      localStorage.setItem('currentCompanyInfo', JSON.stringify({
        id: tenant.id,
        name: tenant.full_name,
        short: tenant.short_name,
        domain: tenant.domain,
      }));
      if (isOwner) localStorage.setItem('companyProfile', JSON.stringify({ ...corp, done: true }));
      setResult({ user, tenant });
      setStep(DONE_STEP);
      message.success('加入公司成功');
    } catch (e) {
      setErrorMsg(e.message || '注册加入失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const enterCompany = () => {
    navigate('/portal', { replace: true });
  };

  const copyDomain = async () => {
    const domain = (result && result.tenant && result.tenant.domain) || (invite && invite.domain) || '';
    try {
      await navigator.clipboard.writeText(`https://${domain}`);
      message.success('公司域名已复制到剪贴板');
    } catch (err) {
      message.success(`公司域名：${domain}`);
    }
  };

  const companyShort = (invite && invite.short_name) || '受邀公司';
  const companyFull = (invite && invite.full_name) || '';
  const companyDomain = (result && result.tenant && result.tenant.domain) || (invite && invite.domain) || '';

  const stepsBar = (
    <div className="jc-steps">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 ? <span className="jc-step-line" /> : null}
          <span className={`jc-step${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}>
            <span className="dot">{i < step ? <CheckCircleFilled /> : i + 1}</span>
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="login-page">
      <div className="lg-card jc-card">
        <div className="lg-form">
          <h2 className="lg-heading">加入公司</h2>
          <p className="lg-heading-sub">确认公司信息并完成身份验证后即可加入</p>

          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}><Spin /></div>
          ) : invalidMsg ? (
            <div className="jc-invalid">
              <CloseCircleFilled className="ic" />
              <h3>邀请链接已失效</h3>
              <p>
                {invalidMsg}
                <br />
                邀请链接有 15 天有效期限制，且被验证一次后立即失效。
                <br />
                请联系邀请您的公司管理员重新生成新的链接。
              </p>
              <button type="button" className="lg-submit" style={{ marginTop: 20 }} onClick={() => navigate('/')}>
                返回登录
              </button>
            </div>
          ) : (
            <>
              {stepsBar}

              <div className="jc-company">
                <div className="jc-company-tag">邀请加入</div>
                <div className="jc-company-name">{companyShort}</div>
                <div className="jc-company-full">{companyFull}</div>
              </div>

              {step === 0 ? (
                <>
                  <div className="jc-kv"><span className="k">公司简称</span><span className="v">{companyShort}</span></div>
                  <div className="jc-kv"><span className="k">公司全称</span><span className="v">{companyFull || '—'}</span></div>
                  {invite && invite.department_name ? <div className="jc-kv"><span className="k">所属部门</span><span className="v">{invite.department_name}</span></div> : null}
                  {invite && invite.role_name ? <div className="jc-kv"><span className="k">功能角色</span><span className="v">{invite.role_name}</span></div> : null}
                  {invite && invite.inviter_name ? <div className="jc-kv"><span className="k">邀请者</span><span className="v">{invite.inviter_name}</span></div> : null}
                  <div className="jc-kv"><span className="k">链接有效期</span><span className="v">15 天（验证一次后失效）</span></div>
                  <button type="button" className="lg-submit" style={{ marginTop: 24 }} onClick={toVerify}>
                    注册并验证
                  </button>
                </>
              ) : null}

              {step === 1 ? (
                <>
                  <div className="jc-seg">
                    <button type="button" className={verifyType === 'mobile' ? 'active' : ''} onClick={() => { setVerifyType('mobile'); setErrorMsg(''); setTip(''); }}>
                      <MobileOutlined /> 手机号
                    </button>
                    <button type="button" className={verifyType === 'email' ? 'active' : ''} onClick={() => { setVerifyType('email'); setErrorMsg(''); setTip(''); }}>
                      <MailOutlined /> 邮箱
                    </button>
                  </div>

                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-id"><span className="req">*</span>{verifyType === 'mobile' ? '手机号' : '邮箱'}</label>
                    <input
                      id="jc-id"
                      className="lg-input"
                      placeholder={verifyType === 'mobile' ? '请输入手机号' : '请输入邮箱'}
                      value={identifier}
                      onChange={(e) => { setIdentifier(e.target.value); setErrorMsg(''); }}
                    />
                  </div>

                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-code"><span className="req">*</span>验证码</label>
                    <div className="lg-code-row">
                      <input
                        id="jc-code"
                        className="lg-input"
                        placeholder="请输入 4 位验证码"
                        maxLength={4}
                        value={vCode}
                        onChange={(e) => { setVCode(e.target.value.replace(/\D/g, '')); setErrorMsg(''); }}
                      />
                      <button type="button" className="lg-code-btn" disabled={countdown > 0} onClick={sendCode}>
                        {countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  <div className="lg-error">{errorMsg || (tip ? <span className="lg-tip">{tip}</span> : '')}</div>

                  <div className="jc-actions">
                    <button type="button" className="jc-ghost" onClick={() => { setStep(0); setErrorMsg(''); setTip(''); }}>上一步</button>
                    <button type="button" className="lg-submit" onClick={toProfile}>下一步</button>
                  </div>
                </>
              ) : null}

              {step === 2 ? (
                <>
                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-name"><span className="req">*</span>姓名 / 工作昵称（即登录账号）</label>
                    <input
                      id="jc-name"
                      className="lg-input"
                      placeholder="请输入真实姓名或工作昵称"
                      value={realName}
                      onChange={(e) => { setRealName(e.target.value); setErrorMsg(''); }}
                    />
                  </div>
                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-pwd"><span className="req">*</span>密码</label>
                    <input
                      id="jc-pwd"
                      className="lg-input"
                      type="password"
                      placeholder="请输入密码（至少 6 位）"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
                    />
                  </div>
                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-confirm"><span className="req">*</span>确认密码</label>
                    <input
                      id="jc-confirm"
                      className="lg-input"
                      type="password"
                      placeholder="请再次输入密码"
                      value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); setErrorMsg(''); }}
                    />
                  </div>

                  <div className="lg-error">{errorMsg}</div>

                  <div className="jc-actions">
                    <button type="button" className="jc-ghost" onClick={() => { setStep(1); setErrorMsg(''); }}>上一步</button>
                    <button type="button" className="lg-submit" onClick={toDone}>完成验证</button>
                  </div>
                </>
              ) : null}

              {isOwner && step === OWNER_STEP ? (
                <>
                  <div className="jc-company" style={{ marginBottom: 6 }}>
                    <div className="jc-company-tag">聚能盈业主</div>
                    <div className="jc-company-name">完善公司信息</div>
                    <div className="jc-company-full">用于确认公司的唯一性与真实性</div>
                  </div>

                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-corp-name"><span className="req">*</span>公司全称</label>
                    <input
                      id="jc-corp-name"
                      className="lg-input"
                      placeholder="请输入公司全称"
                      value={corp.name}
                      onChange={(e) => { setCorp((s) => ({ ...s, name: e.target.value })); setErrorMsg(''); }}
                    />
                  </div>
                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-corp-credit"><span className="req">*</span>统一社会信用代码</label>
                    <input
                      id="jc-corp-credit"
                      className="lg-input"
                      placeholder="请输入统一社会信用代码"
                      value={corp.credit}
                      onChange={(e) => { setCorp((s) => ({ ...s, credit: e.target.value })); setErrorMsg(''); }}
                    />
                  </div>
                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-corp-region">所在地区</label>
                    <input
                      id="jc-corp-region"
                      className="lg-input"
                      placeholder="请输入省 / 市 / 区"
                      value={corp.region}
                      onChange={(e) => { setCorp((s) => ({ ...s, region: e.target.value })); setErrorMsg(''); }}
                    />
                  </div>
                  <div className="lg-field">
                    <label className="lg-label" htmlFor="jc-corp-contact">联系人 / 联系电话</label>
                    <input
                      id="jc-corp-contact"
                      className="lg-input"
                      placeholder="请输入联系人姓名（可选）"
                      value={corp.contact}
                      onChange={(e) => { setCorp((s) => ({ ...s, contact: e.target.value })); setErrorMsg(''); }}
                    />
                    <input
                      id="jc-corp-phone"
                      className="lg-input"
                      style={{ marginTop: 8 }}
                      placeholder="请输入联系电话（可选）"
                      value={corp.phone}
                      onChange={(e) => { setCorp((s) => ({ ...s, phone: e.target.value })); setErrorMsg(''); }}
                    />
                  </div>

                  <div className="lg-error">{errorMsg}</div>

                  <div className="jc-actions">
                    <button type="button" className="jc-ghost" onClick={() => { setStep(2); setErrorMsg(''); }}>上一步</button>
                    <button type="button" className="lg-submit" disabled={submitting} onClick={toCorpDone}>
                      {submitting ? '提交中' : '提交'}
                    </button>
                  </div>
                  <div className="lg-hint">若中途退出页面，可在登录后继续补充完善公司信息。</div>
                </>
              ) : null}

              {step === DONE_STEP ? (
                <>
                  <div className="jc-invalid" style={{ paddingBottom: 0 }}>
                    <CheckCircleFilled className="ic" style={{ color: '#52e8b0' }} />
                    <h3>已成功加入 {companyShort}</h3>
                    <p>
                      {result && result.user && result.user.company_admin
                        ? '您是该公司的首位成员，已自动成为公司管理员。'
                        : '请妥善保存以下公司域名，这是您以后使用系统的访问地址。'}
                    </p>
                  </div>
                  <div className="jc-domain">{companyDomain || '—'}</div>
                  <div className="jc-actions">
                    <button type="button" className="jc-ghost" onClick={copyDomain}><CopyOutlined /> 复制地址</button>
                    <button type="button" className="lg-submit" onClick={enterCompany}>进入公司</button>
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinCompanyPage;
